import React, { useState } from 'react';
import { X, Calendar, MapPin, Clock } from 'lucide-react';
import { formatMatchDateFull } from '../utils/dateFormatting';
import { MatchTypeIcon } from './icons/MatchTypeIcon';
import { getDisplayName } from '../utils/displayName';

interface LineupPlayer {
  id: number;
  name: string;
  display_name?: string;
  tennisrecord_name?: string | null;
  email: string;
}

interface LineupPosition {
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  player1?: LineupPlayer;
  player2?: LineupPlayer | null;
}

interface StrategizeMember {
  id: number;
  name: string;
  display_name?: string;
  email: string;
  gender?: 'male' | 'female' | null;
  is_captain: boolean;
  ntrp_rating?: number | null;
}

interface Match {
  id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  home_away?: string;
  team?: {
    name: string;
    lineup_format?: {
      positions: Array<{
        position_number: number;
        match_type: string;
      }>;
    };
  };
}

interface PublishLineupDialogProps {
  open: boolean;
  onClose: () => void;
  onPublish: (sendEmail: boolean, emailNote: string | null, recipientType: string, recipientIds?: number[]) => Promise<void>;
  isResend?: boolean;
  match?: Match | null;
  lineup?: LineupPosition[];
  teamMembers?: StrategizeMember[];
}

const PublishLineupDialog: React.FC<PublishLineupDialogProps> = ({
  open,
  onClose,
  onPublish,
  isResend = false,
  match,
  lineup = [],
  teamMembers = []
}) => {
  const [publishOption, setPublishOption] = useState<'without_email' | 'with_email'>('with_email');
  const [emailNote, setEmailNote] = useState('');
  const [recipientType, setRecipientType] = useState<'lineup_players' | 'entire_team' | 'specific_people'>('lineup_players');
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to check if a position is singles
  const isSinglesPosition = (matchType: string): boolean => {
    return matchType.includes('singles');
  };

  // Helper function to format match type
  const formatMatchType = (matchType: string): string => {
    if (!matchType) return '';
    let formatted = matchType.replace(/_/g, ' ');
    formatted = formatted.replace(/\bmens\b/gi, "men's");
    formatted = formatted.replace(/\bwomens\b/gi, "women's");
    formatted = formatted.replace(/\bmixed\b/gi, "mixed");
    return formatted.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate specific people selection
    if (publishOption === 'with_email' && recipientType === 'specific_people' && selectedRecipientIds.length === 0) {
      return; // Don't submit if no recipients selected
    }
    
    setIsSubmitting(true);
    try {
      const sendEmail = publishOption === 'with_email';
      const note = sendEmail && emailNote.trim() ? emailNote.trim() : null;
      const recipientIds = recipientType === 'specific_people' ? selectedRecipientIds : undefined;
      await onPublish(sendEmail, note, recipientType, recipientIds);
      // Reset form
      setPublishOption('with_email');
      setEmailNote('');
      setRecipientType('lineup_players');
      setSelectedRecipientIds([]);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error publishing lineup:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setPublishOption('with_email');
      setEmailNote('');
      setRecipientType('lineup_players');
      setSelectedRecipientIds([]);
      onClose();
    }
  };

  const handleRecipientToggle = (memberId: number) => {
    setSelectedRecipientIds(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleRecipientTypeChange = (newType: 'lineup_players' | 'entire_team' | 'specific_people') => {
    setRecipientType(newType);
    if (newType !== 'specific_people') {
      setSelectedRecipientIds([]);
    }
  };

  // Prepare lineup preview data
  const lineupPreviewData = lineup.length > 0 && match ? (() => {
    // Group positions by match type and count occurrences
    const positionsByType = new Map<string, Array<{ position: typeof lineup[0], index: number }>>();
    
    lineup.forEach((position) => {
      const formatPosition = match.team?.lineup_format?.positions.find(
        p => p.position_number === position.position_number
      );
      const matchType = formatPosition?.match_type || 'unknown';
      
      if (!positionsByType.has(matchType)) {
        positionsByType.set(matchType, []);
      }
      positionsByType.get(matchType)!.push({ position, index: positionsByType.get(matchType)!.length + 1 });
    });
    
    // Sort positions by their original position_number to maintain order
    return lineup.map((position) => {
      const formatPosition = match.team?.lineup_format?.positions.find(
        p => p.position_number === position.position_number
      );
      const matchType = formatPosition?.match_type || 'unknown';
      const typePositions = positionsByType.get(matchType) || [];
      const typeIndex = typePositions.findIndex(p => p.position.position_number === position.position_number) + 1;
      const total = typePositions.length;
      
      const formattedType = formatMatchType(matchType);
      const matchTypeDisplay = total > 1 ? `${formattedType} ${typeIndex}` : formattedType;
      
      return {
        position,
        matchType,
        matchTypeDisplay,
        isSingles: isSinglesPosition(matchType)
      };
    });
  })() : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {isResend ? 'Resend Lineup Email' : 'Publish Lineup'}
          </h3>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 2-Column Layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Column - Options */}
          <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-6">
            <form id="publish-lineup-form" onSubmit={handleSubmit} className="space-y-5">
              {/* Match Details */}
              {match && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 mb-5">
                  <div className="space-y-2">
                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                      {match.team?.name || 'Team'} vs {match.opponent_name}
                    </div>
                    <div className="space-y-2">
                      {(() => {
                        const formatted = formatMatchDateFull(match.match_date);
                        return (
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              <span>{formatted.date}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                              <Clock className="w-4 h-4 flex-shrink-0" />
                              <span>{formatted.time}</span>
                            </div>
                          </div>
                        );
                      })()}
                      {match.location && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                          <MapPin className="w-4 h-4" />
                          <span>{match.location}</span>
                        </div>
                      )}
                      {match.home_away && (
                        <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded">
                          {match.home_away === 'home' ? 'Home' : 'Away'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Publish Option */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {isResend ? 'Send Email' : 'Publish Option'}
                </label>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="publishOption"
                      value="with_email"
                      checked={publishOption === 'with_email'}
                      onChange={(e) => setPublishOption(e.target.value as 'without_email' | 'with_email')}
                      className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                      disabled={isSubmitting}
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">
                      {isResend ? 'Send Email' : 'Publish with Email'}
                    </span>
                  </label>
                  {!isResend && (
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="publishOption"
                        value="without_email"
                        checked={publishOption === 'without_email'}
                        onChange={(e) => setPublishOption(e.target.value as 'without_email' | 'with_email')}
                        className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                        disabled={isSubmitting}
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">Publish without Email</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Email Options (shown when "with_email" is selected) */}
              {publishOption === 'with_email' && (
                <>
                  {/* Recipient Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Send to
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="recipientType"
                          value="lineup_players"
                          checked={recipientType === 'lineup_players'}
                          onChange={(e) => handleRecipientTypeChange(e.target.value as 'lineup_players' | 'entire_team' | 'specific_people')}
                          className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-gray-700 dark:text-gray-300">Players in the Lineup</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="recipientType"
                          value="entire_team"
                          checked={recipientType === 'entire_team'}
                          onChange={(e) => handleRecipientTypeChange(e.target.value as 'lineup_players' | 'entire_team' | 'specific_people')}
                          className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-gray-700 dark:text-gray-300">Entire Team</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="recipientType"
                          value="specific_people"
                          checked={recipientType === 'specific_people'}
                          onChange={(e) => handleRecipientTypeChange(e.target.value as 'lineup_players' | 'entire_team' | 'specific_people')}
                          className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-gray-700 dark:text-gray-300">Select Specific People</span>
                      </label>
                    </div>
                  </div>

                  {/* Specific People Selection */}
                  {recipientType === 'specific_people' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Recipients
                      </label>
                      {teamMembers.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No team members available</p>
                      ) : (
                        <div className="border border-gray-300 dark:border-gray-600 rounded-md max-h-48 overflow-y-auto bg-white dark:bg-gray-700">
                          <div className="p-2 space-y-2">
                            {teamMembers.map((member) => {
                              const hasEmail = member.email && member.email.trim() !== '';
                              const isDisabled = !hasEmail || isSubmitting;
                              
                              return (
                                <label
                                  key={member.id}
                                  className={`flex items-center p-2 rounded ${
                                    isDisabled 
                                      ? 'cursor-not-allowed opacity-60' 
                                      : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedRecipientIds.includes(member.id)}
                                    onChange={() => handleRecipientToggle(member.id)}
                                    className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                                    disabled={isDisabled}
                                  />
                                  <div className="ml-2 flex-1">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {getDisplayName(member)}
                                    </div>
                                    <div className={`text-xs ${
                                      hasEmail 
                                        ? 'text-gray-500 dark:text-gray-400' 
                                        : 'text-red-500 dark:text-red-400 italic'
                                    }`}>
                                      {hasEmail ? member.email : 'No email address'}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {recipientType === 'specific_people' && selectedRecipientIds.length === 0 && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          Please select at least one recipient
                        </p>
                      )}
                    </div>
                  )}

                  {/* Email Note */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Additional Note (Optional)
                    </label>
                    <textarea
                      value={emailNote}
                      onChange={(e) => setEmailNote(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Add any additional information or instructions for your team..."
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}
            </form>
          </div>

          {/* Right Column - Preview */}
          <div className="w-1/2 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900/50">
            {match && (
              <div className="space-y-4">
                {/* Lineup Preview */}
                {lineupPreviewData.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Lineup Preview
                    </h4>
                    <div className="space-y-3">
                      {lineupPreviewData.map(({ position, matchType, matchTypeDisplay, isSingles }) => (
                        <div
                          key={position.position_number}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800"
                        >
                          <div className="mb-2">
                            <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1.5">
                              <MatchTypeIcon matchType={matchType} iconSize="w-4 h-4" />
                              {matchTypeDisplay}
                            </h5>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {isSingles ? 'Player:' : 'Player 1:'}
                              </span>
                              {position.player1?.name ? (
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {getDisplayName(position.player1)}
                                </span>
                              ) : (
                                <span className="text-sm font-medium text-gray-400 dark:text-gray-500 italic">
                                  TBD
                                </span>
                              )}
                            </div>
                            {!isSingles && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Player 2:</span>
                                {position.player2?.name ? (
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {getDisplayName(position.player2)}
                                  </span>
                                ) : (
                                  <span className="text-sm font-medium text-gray-400 dark:text-gray-500 italic">
                                    TBD
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No lineup to preview
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="publish-lineup-form"
            disabled={isSubmitting || (publishOption === 'with_email' && recipientType === 'specific_people' && selectedRecipientIds.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            {isSubmitting 
              ? (isResend ? 'Sending...' : 'Publishing...') 
              : (isResend 
                  ? 'Send Email' 
                  : (publishOption === 'with_email' ? 'Publish and Send' : 'Publish')
                )
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishLineupDialog;

